import { Link } from "react-router-dom";
import StatCard from "@/pages/proprietaire/components/StatCard";
import DashboardCard from "@/pages/proprietaire/components/DashboardCard";
import { useDgStats } from "@/hooks/useDgStats";
import { useSession } from "@/session/SessionProvider";
import { fullName } from "@/session/types";
import { proprietaireNomComplet } from "@/pages/agence/proprietaires/types";
import { useProprietaires } from "@/pages/agence/proprietaires/ProprietairesProvider";

export default function AgenceDashboard() {
  const { session } = useSession();
  const firstName = session ? fullName(session).split(" ")[0] : "";
  const { proprietaires } = useProprietaires();
  const { stats } = useDgStats();
  // Les compteurs d'immeubles/biens proviennent EXCLUSIVEMENT de la RPC
  // sécurisée `stats_dashboard_dg()`. On n'utilise aucun repli sur la longueur
  // des listes locales : un « 0 » réel doit rester un « 0 » (pas de masquage
  // d'une statistique serveur par une valeur côté frontend).
  const actifs = proprietaires.filter((item) => item.statut === "actif").length;
  const recents = proprietaires.slice(0, 5);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
            Bonjour{firstName ? ` ${firstName}` : ""}
          </h2>
          <p className="mt-1 text-sm text-foreground-600">
            Voici l'aperçu de votre agence. Commencez par enregistrer vos propriétaires.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Link
            to="/agence/proprietaires"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-user-add-line text-base" aria-hidden="true" />
            Ajouter un propriétaire
          </Link>
          <Link
            to="/agence/immeubles"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-building-2-line text-base" aria-hidden="true" />
            Voir les immeubles
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-user-star-line" label="Propriétaires" value={String(proprietaires.length)} tone="primary" />
        <StatCard icon="ri-checkbox-circle-line" label="Propriétaires actifs" value={String(actifs)} tone="secondary" />
        <StatCard icon="ri-building-2-line" label="Immeubles gérés" value={String(stats.immeubles)} tone="accent" />
        <StatCard icon="ri-home-4-line" label="Biens gérés" value={String(stats.biens)} tone="neutral" />
        <StatCard icon="ri-home-smile-2-line" label="Biens occupés" value={String(stats.biens_occupes)} tone="primary" />
        <StatCard icon="ri-home-8-line" label="Biens disponibles" value={String(stats.biens_disponibles)} tone="secondary" />
        <StatCard icon="ri-file-text-line" label="Contrats actifs" value={String(stats.contrats_actifs)} tone="accent" />
        <StatCard icon="ri-user-3-line" label="Locataires" value={String(stats.locataires)} tone="neutral" />
      </div>

      <DashboardCard
        title="Propriétaires récents"
        icon="ri-user-star-line"
        actionLabel="Voir tous"
        actionTo="/agence/proprietaires"
      >
        {recents.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-md bg-background-100/70 px-6 py-10 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-primary-600">
              <i className="ri-user-star-line text-xl" aria-hidden="true" />
            </span>
            <p className="mt-3 text-sm font-heading font-semibold text-foreground-950">Aucun propriétaire enregistré</p>
            <p className="mt-1 max-w-xs text-xs text-foreground-600">
              Dès que vous ajouterez un propriétaire, il apparaîtra ici avec le nombre d'immeubles et de biens qui lui
              sont rattachés.
            </p>
            <Link
              to="/agence/proprietaires"
              className="mt-5 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              Ajouter un propriétaire
            </Link>
          </div>
        ) : (
          <ul className="flex flex-col divide-y divide-background-200">
            {recents.map((proprietaire) => (
              <li key={proprietaire.id} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                <div className="min-w-0">
                  <p className="truncate text-sm font-label font-medium text-foreground-950">
                    {proprietaireNomComplet(proprietaire)}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-foreground-500">
                    {proprietaire.telephone || proprietaire.email || "Coordonnées non renseignées"}
                  </p>
                </div>
                <Link
                  to={`/agence/proprietaires/${proprietaire.id}`}
                  className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-label font-medium text-primary-700 transition-colors hover:text-primary-800 cursor-pointer"
                >
                  Voir la fiche
                  <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </DashboardCard>
    </div>
  );
}